import React, { useMemo, useEffect, useRef, useState } from "react";
import { connect } from "react-redux";
import { MSpinner } from "components/Shared";
import { CustomerDetailsDto, SurveyTypeListDto, CustomerListModel } from "interfaces/Mueller.Business.Models";
import Button from "@mui/material/Button";
import { MSelectOption } from "components/Shared/MSelect.tsx";
import { getSurveyTypesByCustomer } from "requests/customer";
import CustomerSurveyTypeSelection from "./CustomerSurveyTypeSelection.tsx";
import { getMCardConfiguration, getMCardData, updateMCardData, copySurveyType } from "requests/customerConfiguration";
import { insertValueFieldIntoCards } from "selectors/homeowner/caseForms/caseHeaderV2";
import { INFO_CARD_KEY, BILLING_CARD_KEY, REQUIREMENTS_CARD_KEY } from "components/Customer/Profile/SurveyTypes/CustomerSurveyTypeConfiguration.js";
import MCard, { MCardControlMethods } from "components/Shared/MCard.tsx";
import CustomerSurveyTypeForms from "components/Customer/SurveyTypes/CustomerSurveyTypeForms.tsx"
import CustomerSurveyTypeNotifications from "components/Customer/SurveyTypes/CustomerSurveyTypeNotifications.tsx";
import CustomerSurveyTypeStamps from "components/Customer/SurveyTypes/CustomerSurveyTypeStamps.tsx";

interface CopySurveyTypeWizardProps {
    onSubmit: (newSurveyTypeCode: string) => void;
    onCancel: () => void;
    // Redux
    customerDetails: CustomerDetailsDto;
    customerList: CustomerListModel[];  // This should have been already fetched in CustomerSurveyTypes.tsx
    // Router
    history: any;
}

// If you edit this enum, please update lastStep in CopySurveyTypeWizard if necessary
enum WizardStep {
  SelectSurveyType,
  SurveyTypeInfo,
  Billing,
  Requirements,
  Stamps,
  Forms,
  Notifications,
};

const CopySurveyTypeWizard = (props: CopySurveyTypeWizardProps) => { 
  const [currentStep, setCurrentStep] = useState(WizardStep.SelectSurveyType);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState<MSelectOption>();
  const [selectedSurveyType, setSelectedSurveyType] = useState<MSelectOption>();
  const [confirmSelection, setConfirmSelection] = useState(false);
  const [surveyTypes, setSurveyTypes] = useState<SurveyTypeListDto[]>([]);
  const [mCardData, setMCardData] = useState(null);
  const [mCardConfiguration, setMCardConfiguration] = useState(null);
  const [mCardConfigurationApplied, setMCardConfigurationApplied] = useState(null);
  const cardControlMethods = useRef<MCardControlMethods[]>([]);
  const mounted = useRef(true);

  useEffect(() => {
    return () => {
      mounted.current = false;
    };
  }, []);

  useEffect(() => {
    if (mCardData && mCardConfiguration) {
      let cardsAndData = {
        cards: global.structuredClone(mCardConfiguration),
        caseHeaderDto: mCardData,
      };
      insertValueFieldIntoCards(cardsAndData);
      setMCardConfigurationApplied(cardsAndData.cards);
      setIsLoading(false);
    }
  }, [mCardData, mCardConfiguration]);

  // Step controls

  const lastStep = WizardStep.Notifications;

  const onPrevious = () => {
    switch(currentStep) {
      case WizardStep.SelectSurveyType:
        props.onCancel();
        return;
      case WizardStep.SurveyTypeInfo:
        return; // We don't want to go back after we've copied the survey type
      case WizardStep.Billing:
        if (!mCardIsValid(BILLING_CARD_KEY)) return;
        handleMCardChange(BILLING_CARD_KEY);
        break;
      case WizardStep.Requirements:
        if (!mCardIsValid(REQUIREMENTS_CARD_KEY)) return;
        handleMCardChange(REQUIREMENTS_CARD_KEY);
        break;
    }
    setCurrentStep(currentStep - 1);
  };

  const onNext = () => {
    switch(currentStep) {
      case WizardStep.SelectSurveyType:
        if (!selectedCustomer || !selectedSurveyType || !confirmSelection) return;
        setIsLoading(true);
        const onSuccess = () => {
          // Get CustomerSurveyTypeConfiguration data
          getMCardData(props.customerDetails.number, selectedSurveyType.value, setMCardData, mounted);
          getMCardConfiguration(props.customerDetails.number, selectedSurveyType.value, setMCardConfiguration, mounted);
          setIsLoading(false);
          setCurrentStep(currentStep + 1);
        };
        const onError = () => {
          setConfirmSelection(false);
          setIsLoading(false);
          return;
        };
        copySurveyType(props.customerDetails.number, selectedSurveyType.value, selectedCustomer.value, onSuccess, onError, mounted);
        return;
      case WizardStep.SurveyTypeInfo:
        if (!mCardIsValid(INFO_CARD_KEY)) return;
        handleMCardChange(INFO_CARD_KEY);
        break;
      case WizardStep.Billing:
        if (!mCardIsValid(BILLING_CARD_KEY)) return;
        handleMCardChange(BILLING_CARD_KEY);
        break;
      case WizardStep.Requirements:
        if (!mCardIsValid(REQUIREMENTS_CARD_KEY)) return;
        handleMCardChange(REQUIREMENTS_CARD_KEY);
        break;
    }
    if (currentStep.valueOf() === lastStep.valueOf()) {
      props.onSubmit(`${selectedSurveyType.value}`);
      return;
    }
    setCurrentStep(currentStep + 1);
  };

  // Step 1 SelectSurveyType: CustomerSurveyTypeSelection

  const customerOptions = useMemo(() => {
    let result = [...props.customerList ].sort((a, b) => a.number - b.number);
    return result
      .filter(c => c.number !== props.customerDetails.number)
      .map(c => ({ label: `${c.number} ${c.displayName}${c.inactiveDate ? " (Inactive)" : ""}`, value: c.number }));
  }, [props.customerList]);
  const onCustomerChange = (option: MSelectOption) => {
    setSelectedCustomer(option);
    const handleSurveyTypes = (response: SurveyTypeListDto[]) => setSurveyTypes(response);
    getSurveyTypesByCustomer(option.value, handleSurveyTypes, mounted);
  };
 
  const surveyTypeOptions = useMemo(() => {
    return surveyTypes.map(s => ({ label: s.name, value: s.code }));
  }, [surveyTypes]);
  const onSurveyTypeChange = (option: MSelectOption) => setSelectedSurveyType(option);
  const onConfirmSelection = (value: boolean) => setConfirmSelection(value);
  
  // Steps 2 to 4: CustomerSurveyTypeConfiguration MCards

  const getMCardForKey = (key: string) => {
    let definition = mCardConfigurationApplied?.find(c => c.key === key);
    if (definition) {
      const setCardControlMethods = (controlMethods: MCardControlMethods) => cardControlMethods.current[key] = controlMethods;
      return (
        <MCard
          {...definition}
          key={definition.key}
          cellWidth={12}
          getControlMethods={setCardControlMethods}
          onlyEditable
        />);
    }
  };

  const mCardIsValid = (key: string) => cardControlMethods.current[key].runValidation(true);

  const handleMCardChange = (key: string) => {
      const editedData =  cardControlMethods.current[key].getEditedData();
      if (Object.keys(editedData).length) {
        setIsLoading(true);
        // This logic is borrowed from CustomerSurveyTypeConfiguration.js
        // If you add the QA Queue Rules card to this wizard, you'll need to also include custom validation for that from the file above
        let requestData = {};
        editedData.forEach(d => {
          if (typeof (d.value) === "undefined") {
            requestData[d.key] = null;
          } else {
            requestData[d.key] = d.value;
          }
        });
        const onError = () => {
          setIsLoading(false);
        }
        updateMCardData(props.customerDetails.number, selectedSurveyType.value, requestData, setMCardData, onError, mounted);
      }
  };

  // Render

  const stepToComponent = {
    [WizardStep.SelectSurveyType]: <CustomerSurveyTypeSelection 
                                      onCustomerChange={onCustomerChange}
                                      customerOptions={customerOptions}
                                      selectedCustomer={selectedCustomer}
                                      onSurveyTypeChange={onSurveyTypeChange}
                                      surveyTypeOptions={surveyTypeOptions}
                                      selectedSurveyType={selectedSurveyType}
                                      confirmedSelection={confirmSelection}
                                      onConfirmSelection={onConfirmSelection}
                                      destinationCustomer={`${props.customerDetails.number} ${props.customerDetails.displayName}`}
                                    />,
    [WizardStep.SurveyTypeInfo]: mCardConfigurationApplied?.length ? getMCardForKey(INFO_CARD_KEY) : <></>,
    [WizardStep.Billing]: mCardConfigurationApplied?.length ? getMCardForKey(BILLING_CARD_KEY) : <></>,
    [WizardStep.Requirements]: mCardConfigurationApplied?.length ? getMCardForKey(REQUIREMENTS_CARD_KEY) : <></>,
    [WizardStep.Stamps]: (selectedCustomer && selectedSurveyType) 
                          ? <CustomerSurveyTypeStamps 
                              verticalDisplay={true} 
                              surveyTypeCodeOverride={selectedSurveyType.value}
                            />
                          : <></>,
    [WizardStep.Forms]: (selectedCustomer && selectedSurveyType) 
                        ? <CustomerSurveyTypeForms 
                            verticalDisplay={true} 
                            surveyTypeCodeOverride={selectedSurveyType.value}
                          /> 
                        : <></>,
    [WizardStep.Notifications]: (selectedCustomer && selectedSurveyType) 
                                ? <CustomerSurveyTypeNotifications 
                                    verticalDisplay={true} 
                                    surveyTypeCodeOverride={selectedSurveyType.value}
                                  /> 
                                : <></>,
  };

  return (
    <div className="survey-type-setup-wizard-container">
      <div className="survey-type-setup-wizard-step-container">
        {isLoading ? <MSpinner /> : stepToComponent[currentStep]}
      </div>
      <div className="flex-row-between">
        <Button 
          onClick={onPrevious}
          disabled={currentStep === WizardStep.SurveyTypeInfo}>
            {currentStep === WizardStep.SelectSurveyType ? "Cancel" : "Previous"}
        </Button>
        <Button 
          onClick={onNext}
          disabled={!selectedCustomer || !selectedSurveyType || !confirmSelection}>
            {currentStep.valueOf() === lastStep.valueOf() ? "Finish" : "Next"}
        </Button>
      </div>
    </div>
  );
};

const mapStateToProps = state => ({
  customerList: state.customer.customerList,
  customerDetails: state.customer.customerDetails,
});

export default connect(mapStateToProps, null)(CopySurveyTypeWizard);